import React, {useState, useEffect} from 'react'
import {makeStyles} from '@mui/styles'
import {useLocation, Switch, useHistory, Route, matchPath} from 'react-router-dom'
import Login from './components/Auth/Login'
import ResetPassword from './components/Auth/ResetPassword'
import CreatePassword from './components/Auth/CreatePassword'
import Messages from './components/Messages/Messages'
import AppAuth from './lib/AppAuth'
import CleaningsPage from './components/Services/CleaningsPage'
import DogwalkingPage from './components/Services/DogwalkingPage'
import MassagesPage from './components/Services/MassagesPage'
import UserProfile from './components/ResidentProfiles/UserProfile'
import ViewEventsPage from './components/Events/ViewEventsPage'
import ViewAnnouncementsPage from './components/Announcements/ViewAnnouncementsPage'
import ViewGuestAuthorizationsPage from './components/GuestAuthorization/ViewGuestAuthorizationsPage'
import './App.css'
import {Grid} from '@mui/material'
import Loader from './components/Loader'
import {ThemeProvider, StyledEngineProvider} from '@mui/material/styles'
import {useTheme} from './theme'
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFnsV3' // this relies on package 'date-fns'
import {enUS} from 'date-fns/locale/en-US'
import {LocalizationProvider} from '@mui/x-date-pickers'
import NavPanel, {MobileNavClosedHeight} from './components/NavPanel'
import {useSelector, useDispatch} from 'react-redux'
import {setAuthenticatedUser} from './actions/authActions'
import Routes from './constants/RouteConstants'
import {rxrLightGreyColor} from './assets/styles/color'
import {AMENITY_TYPE_RECREATIONAL, AMENITY_TYPE_OPERATIONAL} from './constants/ModelConstants'
import {CACHE_GlobalAdminOrganizationId} from './constants/AppConstants'

// Components
import BackgroundDataSync from './components/BackgroundDataSync'
import IncomingViewDeliveriesPage from './components/Deliveries/IncomingViewDeliveriesPage'
import OutgoingViewDeliveriesPage from './components/Deliveries/OutgoingViewDeliveriesPage'
import ViewAmenityReservationsPage from './components/Amenities/ViewAmenityReservationsPage'
import CalendarViewPage from './components/Amenities/CalendarViewPage'
import ElevatorCalendarPage from './components/Amenities/ElevatorCalendarPage'
import AmenityManagerPage from './components/Amenities/AmenityManagerPage'
import CreateAmenityPage from './components/Amenities/CreateAmenityPage'
import KYRWidgetContainer from './components/KnowYourResident/KYRWidgetContainer'
import DashboardUsers from './components/Settings/DashboardUsersPage'
import Assistant from './components/RXOAssistant/Assistant'
import ServiceRequestsPage from './components/ServiceRequests/ServiceRequestsPage'
import PersonalTrainingPage from './components/Services/PersonalTrainingPage'
import CommandCenter from './components/CommandCenter/CommandCenter'
import VersionChecker from './components/VersionChecker'
import Onboarding from './components/Onboarding/Onboarding'
import ResidentSentiment from './components/ResidentSentiment/ResidentSentiment'
import ExitSurveys from './components/ExitSurveys/ExitSurveys'
import PartnersPage from './components/Settings/Partners/PartnersPage'
import PromotionsPage from './components/Settings/Promotions/PromotionsPage'
import DummyAccountPage from './components/Settings/DummyAccount/DummyAccountPage'
import StaffProfile from './components/AccountPreferences/StaffProfile'

import {selectAuthedUserId} from './reducers/selectors'
import {ErrorBoundary} from 'react-error-boundary'
import ErrorBoundaryFallback from './components/ErrorBoundaryFallback'
import ForgotPassword from './components/Auth/ForgotPassword'
import AccountPreferences from './components/AccountPreferences/AccountPreferences'
import ResidentAppDocuments from './components/GlobalAdmin/ResidentAppDocuments'
import ResidentAppDocumentsAtBuilding from './components/GlobalAdmin/ResidentAppDocumentsAtBuilding'
import GlobalAdminDashboard from './components/GlobalAdmin/GlobalAdminDashboard'
import UserManagement from './components/GlobalAdmin/UserManagement'
import ChatSettings from './components/GlobalAdmin/ChatSettings/ChatSettings'
import {setGlobalAdminOrganizationId} from './actions/globalAdminActions'

const useStyles = makeStyles(theme => ({
  interfaceContainer: {
    display: 'flex',
    width: '100%',
    height: '100%',
    overflow: 'hidden',
  },
  content: props => ({
    width: '100%',
    height: '100%',
    background: rxrLightGreyColor,
    overflowY: 'auto',
    paddingTop: props.isMobile ? MobileNavClosedHeight : 0,
  }),
  loader: props => ({
    paddingLeft: props.navPanelWidth,
  }),
}))

// these routes are only accessible to unauthenticated users and are the only routes accessible to unauthenticated users
const UN_AUTHED_ROUTES = [Routes.LOGIN, Routes.PASSWORD_CREATE, Routes.PASSWORD_RESET, Routes.PASSWORD_FORGOT]
// these routes should not show the NavPanel
const HIDE_NAV_PANEL_ROUTES = [Routes.ACCOUNT_PREFERENCES]

function App() {
  const dispatch = useDispatch()
  const authedUserId = useSelector(selectAuthedUserId)
  const activeBuilding = useSelector(state => state.Buildings.buildingsLookup[state.Buildings.activeBuildingId])
  const navPanelWidth = useSelector(state => state.App.navPanelWidth)
  const isMobile = useSelector(state => state.App.isMobile)
  const classes = useStyles({navPanelWidth, isMobile})
  const history = useHistory()
  const [isReady, setIsReady] = useState(false)
  const [isLoading, setLoading] = useState(true)

  const {pathname} = useLocation()
  const isAdminMode = matchPath(pathname, {path: Routes.ADMIN_DASHBOARD})
  const showAccountPreferences = !!(
    sessionStorage.getItem('showAccountPreferences') && sessionStorage.getItem('showAccountPreferences') === 'true'
  )
  useEffect(() => {
    //check if there is a currently authenticated user
    AppAuth.Instance()
      .getAuthenticatedUser()
      .then(user => {
        if (user) {
          return setAuthenticatedUser(dispatch, user).then(() => {
            // if we're in admin mode and we know what Org we're on,
            if (isAdminMode && localStorage[CACHE_GlobalAdminOrganizationId]) {
              // initialize the global admin organization in state so that the dashboard can load data
              return setGlobalAdminOrganizationId(dispatch, localStorage[CACHE_GlobalAdminOrganizationId])
            }
          })
        }
      })
      .catch(err => {
        console.error(err)
      })
      .finally(() => {
        setIsReady(true)
        setLoading(false)
      })
  }, [])

  useEffect(() => {
    if (!isReady) {
      return
    }

    // if we're authed and on any pages unauthed pages (or root) we go to Comand Center
    if (authedUserId && [...UN_AUTHED_ROUTES, Routes.ROOT].includes(pathname)) {
      if (showAccountPreferences) {
        history.push(Routes.ACCOUNT_PREFERENCES)
      } else {
        history.push(Routes.COMMAND_CENTER)
      }
      // add the new preferences screen over here as of now
    } else if (!authedUserId && !UN_AUTHED_ROUTES.includes(pathname)) {
      history.push(Routes.LOGIN)
    }
  }, [authedUserId, isReady, pathname])

  const currentTheme = useTheme()

  if (!isReady) {
    return <Loader />
  }

  const shouldShowPanelAndWidget = !HIDE_NAV_PANEL_ROUTES.includes(pathname)

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enUS}>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={currentTheme}>
          <div className={classes.interfaceContainer}>
            {isLoading && <Loader className={classes.loader} />}
            {!!authedUserId && activeBuilding ? (
              <React.Fragment>
                <VersionChecker />
                {shouldShowPanelAndWidget && <NavPanel />}
                <BackgroundDataSync />
                {!isAdminMode && shouldShowPanelAndWidget && <KYRWidgetContainer />}
              </React.Fragment>
            ) : null}
            <ErrorBoundary FallbackComponent={ErrorBoundaryFallback}>
              <Grid className={classes.content}>
                {/*
              We're using a switch here because we only ever want one to render at a time.
              So the first matching route it finds will be the one that is displayed.
              Because of this, the order is important.
              For best results: place more descriptive routes above less descriptive routes
              For readability: please keep similar routes grouped together
              */}

                <Switch>
                  {/* ROOT ----------------------------------------------------------------- */}
                  <Route exact path={Routes.ROOT} render={() => <CommandCenter />} />

                  {/* LOGIN ----------------------------------------------------------------- */}
                  <Route path={Routes.LOGIN} render={() => <Login />} />

                  {/* PASSWORD FLOWS ----------------------------------------------------------------- */}
                  <Route path={Routes.PASSWORD_RESET} render={() => <ResetPassword />} />
                  <Route path={Routes.PASSWORD_CREATE} render={() => <CreatePassword />} />
                  <Route path={Routes.PASSWORD_FORGOT} render={() => <ForgotPassword />} />

                  {/* ACCOUNT PREFERENCES ------------------------------------------------------- */}
                  <Route path={Routes.ACCOUNT_PREFERENCES} render={() => <AccountPreferences />} />

                  {/* COMMAND CENTER ----------------------------------------------------------- */}
                  <Route path={Routes.COMMAND_CENTER} render={() => <CommandCenter />} />

                  {/* SERVICES ----------------------------------------------------------------- */}
                  <Route path={Routes.CLEANING} render={() => <CleaningsPage />} />
                  <Route path={Routes.DOGWALKING} render={() => <DogwalkingPage />} />
                  <Route path={Routes.MASSAGE} render={() => <MassagesPage />} />
                  <Route path={Routes.PERSONALTRAINING} render={() => <PersonalTrainingPage />} />

                  {/* MESSAGES ----------------------------------------------------------------- */}
                  <Route path={Routes.MESSAGES} render={() => <Messages />} />

                  {/* EVENTS ----------------------------------------------------------------- */}
                  <Route path={Routes.EVENTS} render={() => <ViewEventsPage />} />

                  {/* RESIDENT PROFILE ----------------------------------------------------------------- */}
                  <Route path={Routes.PROFILE} render={() => <UserProfile />} />

                  {/* STAFF PROFILE ----------------------------------------------------------------- */}
                  <Route path={Routes.STAFF_PROFILE} render={() => <StaffProfile />} />

                  {/* ANNOUNCEMENTS ----------------------------------------------------------------- */}
                  <Route path={Routes.ANNOUNCEMENTS} render={() => <ViewAnnouncementsPage />} />

                  {/* GUEST AUTH ----------------------------------------------------------------- */}
                  <Route path={Routes.GUESTAUTHORIZATION} render={() => <ViewGuestAuthorizationsPage />} />

                  {/* DELIVERIES ----------------------------------------------------------------- */}
                  <Route path={Routes.DELIVERIES_IN} render={() => <IncomingViewDeliveriesPage />} />
                  <Route path={Routes.DELIVERIES_OUT} render={() => <OutgoingViewDeliveriesPage />} />

                  {/* SERVICE REQUESTS ----------------------------------------------------------------- */}
                  <Route path={Routes.SERVICE_REQUESTS} render={() => <ServiceRequestsPage />} />

                  {/* AMENITY ----------------------------------------------------------------- */}
                  <Route
                    path={Routes.RECREATIONAL_AMENITIES_RESERVATIONS}
                    render={() => <ViewAmenityReservationsPage amenityType={AMENITY_TYPE_RECREATIONAL} />}
                  />
                  <Route
                    path={Routes.OPERATIONAL_AMENITIES_RESERVATIONS}
                    render={() => <ViewAmenityReservationsPage amenityType={AMENITY_TYPE_OPERATIONAL} />}
                  />
                  <Route
                    path={Routes.RECREATIONAL_AMENITIES_CALENDAR_VIEW}
                    render={() => <CalendarViewPage amenityType={AMENITY_TYPE_RECREATIONAL} />}
                  />
                  <Route
                    path={Routes.OPERATIONAL_AMENITIES_CALENDAR_VIEW}
                    render={() => <ElevatorCalendarPage amenityType={AMENITY_TYPE_OPERATIONAL} />}
                  />
                  <Route
                    exact
                    path={Routes.RECREATIONAL_AMENITIES_MANAGER}
                    render={() => <AmenityManagerPage amenityType={AMENITY_TYPE_RECREATIONAL} />}
                  />
                  <Route
                    exact
                    path={Routes.OPERATIONAL_AMENITIES_MANAGER}
                    render={() => <AmenityManagerPage amenityType={AMENITY_TYPE_OPERATIONAL} />}
                  />
                  <Route
                    exact
                    path={Routes.AMENITIES_CREATE_RECREATIONAL}
                    render={() => <CreateAmenityPage isCreate={true} amenityType={AMENITY_TYPE_RECREATIONAL} />}
                  />
                  <Route
                    exact
                    path={Routes.AMENITIES_CREATE_OPERATIONAL}
                    render={() => <CreateAmenityPage isCreate={true} amenityType={AMENITY_TYPE_OPERATIONAL} />}
                  />
                  <Route
                    exact
                    path={Routes.AMENITIES_EDIT_RECREATIONAL}
                    render={() => <CreateAmenityPage amenityType={AMENITY_TYPE_RECREATIONAL} />}
                  />
                  <Route
                    exact
                    path={Routes.AMENITIES_EDIT_OPERATIONAL}
                    render={() => <CreateAmenityPage amenityType={AMENITY_TYPE_OPERATIONAL} />}
                  />

                  {/* DASHBOARD ----------------------------------------------------------------- */}
                  <Route path={Routes.SETTINGS_DASHBOARD_USERS} render={() => <DashboardUsers />} />
                  <Route path={Routes.SETTINGS_PARTNERS} render={() => <PartnersPage />} />
                  <Route path={Routes.SETTINGS_PROMOTIONS} render={() => <PromotionsPage />} />
                  <Route path={Routes.SETTINGS_DUMMY_ACCOUNT} render={() => <DummyAccountPage />} />

                  {/* RXO ASSISTANT ----------------------------------------------------------------- */}
                  <Route path={Routes.RXO_ASSISTANT} render={() => <Assistant />} />

                  {/* ONBOARDING ----------------------------------------------------------------- */}
                  <Route path={Routes.ONBOARDING} render={() => <Onboarding />} />

                  {/* RESIDENT SENTIMENT ----------------------------------------------------------------- */}
                  <Route path={Routes.RESIDENT_SENTIMENT} render={() => <ResidentSentiment />} />

                  {/* EXIT SURVEYS ----------------------------------------------------------------- */}
                  <Route path={Routes.EXIT_SURVEYS} render={() => <ExitSurveys />} />

                  {/* ADMIN DASHBOARD ----------------------------------------------------------------- */}
                  <Route exact path={Routes.ADMIN_DASHBOARD} render={() => <GlobalAdminDashboard />} />

                  <Route path={Routes.ADMIN_DASHBOARD_USER_MANAGEMENT} render={() => <UserManagement />} />

                  <Route
                    exact
                    path={Routes.ADMIN_DASHBOARD_DOCUMENTS_BY_BUILDING_VIEW_SINGLE}
                    render={() => <ResidentAppDocumentsAtBuilding />}
                  />
                  <Route exact path={Routes.ADMIN_DASHBOARD_DOCUMENTS_CREATE_BY_BUILDING} render={() => <ResidentAppDocuments />} />
                  <Route
                    exact
                    path={Routes.ADMIN_DASHBOARD_DOCUMENTS_CREATE_BY_BUILDING_VIEW_SINGLE}
                    render={() => <ResidentAppDocumentsAtBuilding />}
                  />
                  <Route
                    exact
                    path={Routes.ADMIN_DASHBOARD_DOCUMENTS_VIEW_SINGLE_BY_BUILDING_VIEW_SINGLE}
                    render={() => <ResidentAppDocumentsAtBuilding />}
                  />
                  <Route path={Routes.ADMIN_DASHBOARD_DOCUMENTS} render={() => <ResidentAppDocuments />} />
                  <Route path={Routes.ADMIN_DASHBOARD_CHAT_SETTINGS} render={() => <ChatSettings />} />
                </Switch>
              </Grid>
            </ErrorBoundary>
          </div>
        </ThemeProvider>
      </StyledEngineProvider>
    </LocalizationProvider>
  )
}

export default App
